import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash, PencilSimple, Check, X, Prohibit, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import VoidInvoiceModal from "@/components/VoidInvoiceModal";
import { money } from "@/lib/money";

export default function InvoiceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [inv, setInv] = useState(null);
  const [amt, setAmt] = useState("");
  const [method, setMethod] = useState("check");
  const [editing, setEditing] = useState(false);
  const [draftLines, setDraftLines] = useState([]);
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaDraft, setMetaDraft] = useState({});
  const [voidOpen, setVoidOpen] = useState(false);
  const [releaseBusy, setReleaseBusy] = useState(false);
  const [tsData, setTsData] = useState({ attached: [], available: [] });
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachSel, setAttachSel] = useState([]);
  const [tsBusy, setTsBusy] = useState(false);
  const load = () => api.get(`/invoices/${id}`).then((r) => setInv(r.data));
  const loadTs = () => api.get(`/invoices/${id}/timesheets`).then((r) => setTsData(r.data)).catch(() => {});
  useEffect(() => { load(); loadTs(); /* eslint-disable-next-line */ }, [id]);
  if (!inv) return <div className="p-4 md:p-8">Loading…</div>;
  const isAdmin = user.role === "admin";
  const canEdit = isAdmin && inv.status !== "voided";
  const canEditLines = isAdmin && inv.status === "draft";

  const send = async () => {
    try {
      const { data } = await api.post(`/invoices/${id}/send`);
      if (data.email_sent === false) toast.error(`Invoice marked sent, but the email FAILED: ${data.email_error || "unknown error"}. Use "Re-send email" to retry.`);
      else toast.success("Invoice emailed to the client.");
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    load();
  };
  const resend = async () => {
    if (!window.confirm(`Re-send invoice ${inv?.number} to the client's billing email now?`)) return;
    try {
      const { data } = await api.post(`/invoices/${id}/send`);
      if (data.email_sent) toast.success("Invoice re-sent to the client.");
      else toast.error(`Send failed: ${data.email_error || "unknown error"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
  };
  const pay = async (e) => {
    e.preventDefault();
    await api.post(`/invoices/${id}/payments`, { amount: parseFloat(amt), method });
    setAmt(""); load();
  };

  const doDelete = async () => {
    if (!window.confirm(`Delete draft invoice ${inv.number}? Any linked timesheets will be released back to approved so you can re-invoice them.`)) return;
    try {
      const { data } = await api.delete(`/invoices/${id}`);
      toast.success(`Draft deleted. Released ${data.released_timesheets} timesheet(s).`);
      nav("/invoices");
    } catch (e2) { toast.error(e2.response?.data?.detail?.toString() || e2.message); }
  };

  const unvoid = async () => {
    const paidAmt = (inv.payments || []).reduce((t, p) => t + Number(p.amount || 0), 0);
    if (!window.confirm(`Un-void invoice ${inv.number}?\n\nIt goes back to "${inv.void_prior_status || "sent"}"${paidAmt > 0 ? ` (payments on record: $${paidAmt.toFixed(2)})` : ""} with its balance restored, and it counts as outstanding again. Timesheets that were released and are still free are re-attached; any already on another invoice stay there.`)) return;
    setReleaseBusy(true);
    try {
      const { data } = await api.post(`/invoices/${id}/unvoid`);
      toast.success(`Invoice ${inv.number} un-voided → ${data.status.replace(/_/g, " ")}, balance $${Number(data.balance_due).toFixed(2)}${data.reattached_timesheets ? ` · ${data.reattached_timesheets} timesheet(s) re-attached` : ""}${data.timesheets_elsewhere ? ` · ${data.timesheets_elsewhere} already on another invoice` : ""}.`, { duration: 8000 });
      load(); loadTs();
    } catch (e2) { toast.error(e2.response?.data?.detail?.toString() || e2.message); }
    finally { setReleaseBusy(false); }
  };

  const releaseVoided = async () => {
    const n = (inv.timesheet_ids || []).length;
    if (!window.confirm(`Release the ${n} timesheet(s) kept on voided invoice ${inv.number}?\n\nThey go back to "approved" and will show as ready to invoice again.`)) return;
    setReleaseBusy(true);
    try {
      const { data } = await api.post(`/invoices/${id}/release-timesheets`);
      toast.success(`Released ${data.released_timesheets} timesheet(s) — they can be re-invoiced.`);
      load(); loadTs();
    } catch (e2) { toast.error(e2.response?.data?.detail?.toString() || e2.message); }
    finally { setReleaseBusy(false); }
  };

  const detachTs = async (t) => {
    if (!window.confirm(`Remove ${t.worker_name}'s week of ${t.week_start} from this invoice?\n\nIts lines come off the invoice and the timesheet goes back to "approved" so it can be re-invoiced. Worker pay is NOT affected.`)) return;
    setTsBusy(true);
    try {
      await api.post(`/invoices/${id}/timesheets/${t.id}/detach`);
      toast.success("Timesheet removed — released back to approved.");
      load(); loadTs();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setTsBusy(false); }
  };
  const attachTs = async () => {
    setTsBusy(true);
    try {
      const { data } = await api.post(`/invoices/${id}/timesheets/attach`, { timesheet_ids: attachSel });
      toast.success(`Attached ${data.attached} timesheet(s) — lines added.`);
      setAttachOpen(false); setAttachSel([]); load(); loadTs();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setTsBusy(false); }
  };

  const startEdit = () => {
    setDraftLines(inv.lines.map((l) => ({ ...l })));
    setEditing(true);
  };
  const cancelEdit = () => { setEditing(false); setDraftLines([]); };
  const addLine = () => setDraftLines([...draftLines, { kind: "manual", description: "", quantity: 1, rate: 0, amount: 0 }]);
  const updateLine = (i, patch) => {
    const c = [...draftLines];
    c[i] = { ...c[i], ...patch };
    if ("quantity" in patch || "rate" in patch) {
      c[i].amount = Number((parseFloat(c[i].quantity) || 0) * (parseFloat(c[i].rate) || 0));
    }
    setDraftLines(c);
  };
  const removeLine = (i) => setDraftLines(draftLines.filter((_, j) => j !== i));
  const saveLines = async () => {
    const payload = { lines: draftLines.map((l) => ({
      kind: l.kind || "manual",
      description: l.description || "",
      quantity: parseFloat(l.quantity) || 0,
      rate: parseFloat(l.rate) || 0,
      amount: parseFloat(l.amount) || 0,
      timesheet_id: l.timesheet_id || null,
    })) };
    await api.patch(`/invoices/${id}/lines`, payload);
    setEditing(false); load();
  };
  const draftSubtotal = draftLines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      {inv._email_failed && (
        <div data-testid="email-failed-banner"
             className="mb-6 border border-red-300 bg-red-50 rounded-md p-4 flex items-start gap-3">
          <WarningCircle size={22} weight="duotone" className="text-red-600 mt-0.5 shrink-0" />
          <div className="text-sm text-red-800">
            <div className="font-bold uppercase tracking-widest text-xs text-red-700">Email delivery failed</div>
            <div className="mt-1">
              The last invoice email {inv._last_email_status === "failed" ? "could not be sent" : `was reported "${inv._last_email_status}"`}
              {inv._last_email_at ? ` (${new Date(inv._last_email_at).toLocaleString()})` : ""} — the client may not have received this invoice.
            </div>
            <div className="mt-1 text-xs text-red-600">Verify the client's billing email on the Clients tab, then use <b>Re-send email</b>.</div>
          </div>
        </div>
      )}
      {inv.status !== "voided" && inv.unvoid_note && (
        <div data-testid="unvoided-note" className="mb-4 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
          Restored from void by <b>{inv.unvoided_by || "—"}</b>{inv.unvoided_at ? ` on ${new Date(inv.unvoided_at).toLocaleDateString()}` : ""} — {inv.unvoid_note}
        </div>
      )}
      {inv.status === "voided" && (
        <div data-testid="voided-banner"
             className="mb-6 border border-slate-400 bg-slate-100 rounded-md p-4 flex items-start gap-3">
          <WarningCircle size={22} weight="duotone" className="text-slate-700 mt-0.5 shrink-0" />
          <div className="text-sm text-slate-800 flex-1">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="font-bold uppercase tracking-widest text-xs text-slate-600">Voided{inv.void_prior_status ? ` (was ${inv.void_prior_status.replace(/_/g, " ")})` : ""}</div>
              {isAdmin && (
                <button data-testid="unvoid-invoice-btn" onClick={unvoid} disabled={releaseBusy}
                  title="Voided by mistake, or a payment arrived? Restore the invoice."
                  className="h-8 px-3 text-xs font-bold border border-slate-400 rounded-md bg-white hover:bg-slate-50 disabled:opacity-50">
                  {releaseBusy ? "Working…" : "Un-void (restore)"}
                </button>
              )}
            </div>
            <div className="mt-1"><b>Reason:</b> {inv.void_reason || "—"}</div>
            <div className="mt-1 text-xs text-slate-500">
              Voided by <b>{inv.voided_by || "—"}</b>
              {inv.voided_at && ` on ${new Date(inv.voided_at).toLocaleString()}`}
              . Balance zeroed; kept in records under Invoices → Voided.
            </div>
            {(inv.timesheet_ids || []).length > 0 && (
              inv.void_released_timesheets === false ? (
                <div className="mt-2 flex items-center gap-3 flex-wrap" data-testid="voided-ts-retained">
                  <span className="text-xs text-slate-700"><b>{inv.timesheet_ids.length} timesheet(s) kept on this invoice</b> — they will not be billed again.</span>
                  {isAdmin && (
                    <button data-testid="release-voided-ts-btn" onClick={releaseVoided} disabled={releaseBusy}
                      className="h-8 px-3 text-xs font-bold border border-slate-400 rounded-md bg-white hover:bg-slate-50 disabled:opacity-50">
                      {releaseBusy ? "Releasing…" : "Release for re-invoicing"}
                    </button>
                  )}
                </div>
              ) : (
                <div className="mt-2 text-xs text-slate-700" data-testid="voided-ts-released">
                  {inv.timesheet_ids.length} linked timesheet(s) were released back to <b>approved</b>{inv.void_timesheets_released_at ? ` on ${new Date(inv.void_timesheets_released_at).toLocaleDateString()}` : ""} and can be re-invoiced.
                </div>
              )
            )}
          </div>
        </div>
      )}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="tinylabel">Invoice</div>
          <h1 className="text-3xl font-black font-mono">{inv.number}</h1>
          <div className="mt-2 flex items-center gap-3"><StatusPill status={inv.status} /><span className="text-slate-500">Due {inv.due_date} · {inv.payment_terms}</span></div>
        </div>
        <div className="flex gap-2">
          {inv.status === "draft" && isAdmin && <button data-testid="invoice-send-btn" onClick={send} className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md">Mark sent + email</button>}
          {isAdmin && ["sent", "partially_paid", "overdue", "paid"].includes(inv.status) && (
            <button data-testid="invoice-resend-btn" onClick={resend}
              className="h-11 px-4 border border-slate-300 rounded-md font-semibold hover:bg-slate-50">Re-send email</button>
          )}
          {isAdmin && ["sent", "partially_paid", "overdue"].includes(inv.status) && (
            <button
              data-testid="invoice-remind-detail-btn"
              onClick={async () => {
                try {
                  const { data } = await api.post(`/invoices/${id}/remind`);
                  toast.success(`Reminder sent to ${data.to}.`);
                  load();
                } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
              }}
              className={`h-11 px-4 font-semibold rounded-md text-white ${inv.status === "overdue" ? "bg-rose-600 hover:bg-rose-700" : "bg-green-600 hover:bg-green-700"}`}
            >Send payment reminder</button>
          )}
          <a href={`${API_BASE}/invoices/${id}/pdf`} target="_blank" rel="noreferrer" className="h-11 px-4 border border-slate-300 rounded-md font-semibold flex items-center hover:bg-slate-50">Download PDF</a>
          {isAdmin && inv.status === "draft" && (
            <button data-testid="invoice-delete-btn" onClick={doDelete}
              className="h-11 px-4 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md flex items-center gap-2">
              <Trash size={16} /> Delete draft
            </button>
          )}
          {isAdmin && ["draft", "sent", "partially_paid", "overdue"].includes(inv.status) && (
            <button data-testid="invoice-void-btn" onClick={() => setVoidOpen(true)}
              title="Keep this invoice on record as voided"
              className="h-11 px-4 bg-slate-700 hover:bg-slate-800 text-white font-semibold rounded-md flex items-center gap-2">
              <Prohibit size={16} /> Void
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="tinylabel">Details</div>
          {canEdit && !editingMeta && (
            <button data-testid="edit-invoice-meta-btn" onClick={() => { setMetaDraft({
              project_name: inv.project_name ?? inv._project?.name ?? "",
              project_description: inv.project_description ?? inv._project?.description ?? "",
              project_location: inv.project_location ?? inv._project?.location ?? "",
              po_number: inv.po_number ?? inv._project?.po_number ?? "",
              subcontractor_number: inv.subcontractor_number ?? inv._project?.subcontractor_number ?? "",
              payment_terms: inv.payment_terms || "Net 30",
              due_date: inv.due_date || "",
              week_ending: inv.week_ending || "",
            }); setEditingMeta(true); }} className="text-green-600 font-semibold flex items-center gap-1 text-sm"><PencilSimple size={16} /> Edit details</button>
          )}
          {editingMeta && (
            <div className="flex gap-2">
              <button onClick={() => setEditingMeta(false)} className="text-sm font-semibold text-slate-600 flex items-center gap-1"><X size={16} /> Cancel</button>
              <button data-testid="save-invoice-meta-btn" onClick={async () => {
                try {
                  await api.patch(`/invoices/${id}/meta`, metaDraft);
                  toast.success("Invoice details updated.");
                  setEditingMeta(false); load();
                } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
              }} className="text-sm font-semibold text-white bg-green-600 hover:bg-green-700 px-3 py-1 rounded flex items-center gap-1"><Check size={16} /> Save</button>
            </div>
          )}
        </div>
        {!editingMeta ? (
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div><div className="tinylabel">Bill To</div><div className="font-semibold">{inv._client?.name}</div><div className="text-slate-500">{inv._client?.billing_email}</div></div>
            <div><div className="tinylabel">Project</div><div className="font-semibold">{inv.project_name || inv._project?.name}</div>
              {(inv.project_description || inv._project?.description) && <div className="text-slate-500 mt-1">{inv.project_description || inv._project?.description}</div>}
            </div>
            <div><div className="tinylabel">PO #</div><div className="font-mono">{inv.po_number || inv._project?.po_number || "—"}</div></div>
            <div><div className="tinylabel">Sub / Project #</div><div className="font-mono">{inv.subcontractor_number || inv._project?.subcontractor_number || "—"}</div></div>
            <div><div className="tinylabel">Location</div><div>{inv.project_location || inv._project?.location || "—"}</div></div>
            <div><div className="tinylabel">Terms · Due</div><div>{inv.payment_terms} · {inv.due_date}</div></div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[
              ["project_name", "Project name"],
              ["po_number", "PO #"],
              ["subcontractor_number", "Sub / Project #"],
              ["project_location", "Location"],
              ["project_description", "Description"],
              ["payment_terms", "Terms"],
            ].map(([k, label]) => (
              <div key={k}>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</label>
                <input value={metaDraft[k] || ""} onChange={(e) => setMetaDraft({ ...metaDraft, [k]: e.target.value })}
                  placeholder={k === "po_number" || k === "subcontractor_number" ? "TBD if unknown" : ""}
                  data-testid={`meta-input-${k}`}
                  className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md" />
              </div>
            ))}
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Week ending</label>
              <input type="date" value={metaDraft.week_ending || ""} onChange={(e) => setMetaDraft({ ...metaDraft, week_ending: e.target.value })}
                data-testid="meta-input-week_ending" className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md" />
              <div className="mt-1 text-xs text-slate-500">Latest Sunday covered by this invoice (shown in the AR list).</div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Due date</label>
              <input type="date" value={metaDraft.due_date || ""} onChange={(e) => setMetaDraft({ ...metaDraft, due_date: e.target.value })}
                data-testid="meta-input-due_date" className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md" />
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md mt-4 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <div className="tinylabel">Line items</div>
          {canEdit && !editing && (
            <button data-testid="edit-invoice-lines-btn" onClick={startEdit} className="text-green-600 font-semibold flex items-center gap-1 text-sm"><PencilSimple size={16} /> Edit lines</button>
          )}
          {editing && (
            <div className="flex gap-2">
              <button data-testid="add-invoice-line-btn" onClick={addLine} className="text-sm font-semibold text-green-600 flex items-center gap-1"><Plus size={16} /> Add line</button>
              <button data-testid="cancel-edit-lines-btn" onClick={cancelEdit} className="text-sm font-semibold text-slate-600 flex items-center gap-1"><X size={16} /> Cancel</button>
              <button data-testid="save-invoice-lines-btn" onClick={saveLines} className="text-sm font-semibold text-white bg-green-600 hover:bg-green-700 px-3 py-1 rounded flex items-center gap-1"><Check size={16} /> Save</button>
            </div>
          )}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-white text-xs uppercase tracking-widest text-slate-500">
            <tr><th className="text-left px-4 py-3">Description</th><th className="text-right px-4 py-3">Qty</th><th className="text-right px-4 py-3">Rate</th><th className="text-right px-4 py-3">Amount</th>{editing && <th></th>}</tr>
          </thead>
          <tbody>
            {!editing && inv.lines.map((l, i) => (
              <tr key={i} className="border-t border-slate-100"><td className="px-4 py-3">{l.description}</td><td className="text-right px-4 py-3 font-mono">{l.quantity}</td><td className="text-right px-4 py-3 font-mono">{money(l.rate)}</td><td className="text-right px-4 py-3 font-mono">{money(l.amount)}</td></tr>
            ))}
            {editing && draftLines.map((l, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`edit-line-${i}`}>
                <td className="px-2 py-2"><input value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} className="w-full h-10 px-2 border border-slate-300 rounded" /></td>
                <td className="px-2 py-2 text-right"><input type="number" step="0.01" value={l.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })} className="w-24 h-10 px-2 border border-slate-300 rounded text-right font-mono" /></td>
                <td className="px-2 py-2 text-right"><input type="number" step="0.01" value={l.rate} onChange={(e) => updateLine(i, { rate: e.target.value })} className="w-24 h-10 px-2 border border-slate-300 rounded text-right font-mono" /></td>
                <td className="px-2 py-2 text-right font-mono">{money(l.amount)}</td>
                <td className="px-2 py-2 text-right"><button onClick={() => removeLine(i)} className="text-red-600 p-1"><Trash size={16} /></button></td>
              </tr>
            ))}
            <tr className="border-t border-slate-200 bg-slate-50"><td colSpan={3} className="text-right px-4 py-3 font-bold">Subtotal</td><td className="text-right px-4 py-3 font-mono font-bold">{money(editing ? draftSubtotal : inv.subtotal)}</td>{editing && <td></td>}</tr>
            {!editing && <tr className="bg-slate-50"><td colSpan={3} className="text-right px-4 py-3 font-bold">Balance Due</td><td className="text-right px-4 py-3 font-mono font-bold">{money(inv.balance_due)}</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-slate-200 rounded-md mt-4 overflow-hidden" data-testid="invoice-timesheets-card">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <div className="tinylabel">Attached timesheets ({tsData.attached.length})</div>
          {canEditLines && (
            <button data-testid="attach-timesheet-btn" onClick={() => { setAttachSel([]); setAttachOpen(true); }}
              className="text-green-600 font-semibold flex items-center gap-1 text-sm"><Plus size={16} /> Attach timesheet</button>
          )}
        </div>
        {tsData.attached.length === 0 ? (
          <div className="px-4 py-4 text-sm text-slate-400">No timesheets attached — this invoice's lines are manual.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-slate-500">
              <tr><th className="text-left px-4 py-2">Worker</th><th className="text-left px-4 py-2">Project</th><th className="text-left px-4 py-2">Week of</th><th className="text-right px-4 py-2">Hours</th><th className="text-right px-4 py-2">Per diem</th>{canEditLines && <th></th>}</tr>
            </thead>
            <tbody>
              {tsData.attached.map((t) => (
                <tr key={t.id} className="border-t border-slate-100" data-testid={`attached-ts-${t.id}`}>
                  <td className="px-4 py-2 font-semibold">{t.worker_name}{t.billing_only && <span className="ml-2 pill pill-billing-only">BILLING ONLY</span>}</td>
                  <td className="px-4 py-2 text-slate-600">{t.project_name}</td>
                  <td className="px-4 py-2 font-mono">{t.week_start}</td>
                  <td className="px-4 py-2 text-right font-mono">{t.total_hours}</td>
                  <td className="px-4 py-2 text-right font-mono">{t.per_diem_days}</td>
                  {canEditLines && (
                    <td className="px-4 py-2 text-right">
                      <button data-testid={`detach-ts-${t.id}`} disabled={tsBusy} onClick={() => detachTs(t)}
                        className="text-red-600 text-xs font-bold hover:underline disabled:opacity-50">Remove</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {canEditLines && (
          <div className="px-4 py-2 text-xs text-slate-400 border-t border-slate-100">Removing a timesheet deletes its lines from this invoice and releases it back to "approved". Worker pay comes from the timesheet, never the invoice — attach/remove and line edits don't change what the worker is paid.</div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md mt-4 p-6">
        <div className="tinylabel mb-2">Payments</div>
        {inv.payments.length === 0 && <div className="text-slate-400 text-sm">No payments recorded.</div>}
        <ul className="space-y-1">
          {inv.payments.map((p) => (<li key={p.id} className="flex justify-between text-sm border-b border-slate-100 py-2"><span>{p.date} · {p.method}</span><span className="font-mono">{money(p.amount)}</span></li>))}
        </ul>
        {inv.balance_due > 0 && isAdmin && (
          <form onSubmit={pay} className="mt-4 flex flex-wrap gap-3 items-end">
            <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Amount</label>
              <input required type="number" step="0.01" value={amt} onChange={(e) => setAmt(e.target.value)}
                data-testid="payment-amount" className="mt-1 w-40 h-11 px-3 border border-slate-300 rounded-md" /></div>
            <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Method</label>
              <select value={method} onChange={(e) => setMethod(e.target.value)}
                className="mt-1 w-40 h-11 px-3 border border-slate-300 rounded-md"><option>check</option><option>ach</option><option>wire</option><option>card</option></select></div>
            <button data-testid="record-payment-btn" className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md">Record payment</button>
          </form>
        )}
      </div>

      {inv.reminders && inv.reminders.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md mt-4 p-6" data-testid="reminder-history">
          <div className="tinylabel mb-2">Reminder history</div>
          <ul className="space-y-1">
            {inv.reminders.map((r, i) => (
              <li key={i} className="flex justify-between text-sm border-b border-slate-100 py-2">
                <span>{new Date(r.sent_at).toLocaleString()} · to {r.to} · by {r.by}</span>
                <span className="font-mono text-slate-500">{r.days_overdue > 0 ? `${r.days_overdue}d overdue` : "on time"} · bal {money(r.balance_at_time)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {attachOpen && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50" data-testid="attach-ts-modal">
          <div className="bg-white rounded-md w-full max-w-lg p-6 border border-slate-200 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Attach approved timesheets</h2>
              <button type="button" onClick={() => setAttachOpen(false)}><X size={20} /></button>
            </div>
            {tsData.available.length === 0 ? (
              <div className="text-sm text-slate-500" data-testid="attach-empty-note">No approved, un-invoiced timesheets for this client. Create one from the Timesheets tab ("New timesheet" with manual approval works for special circumstances) and it will show up here.</div>
            ) : (
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-md">
                {tsData.available.map((t) => (
                  <label key={t.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`available-ts-${t.id}`}>
                    <input type="checkbox" checked={attachSel.includes(t.id)}
                      onChange={(e) => setAttachSel(e.target.checked ? [...attachSel, t.id] : attachSel.filter((x) => x !== t.id))} />
                    <span className="font-semibold">{t.worker_name}</span>
                    {t.billing_only && <span className="pill pill-billing-only">BILLING ONLY</span>}
                    <span className="text-slate-500">{t.project_name}</span>
                    <span className="ml-auto font-mono text-slate-600 text-xs">w/o {t.week_start} · {t.total_hours}h · {t.per_diem_days} PD</span>
                  </label>
                ))}
              </div>
            )}
            <div className="mt-5 flex gap-3 justify-end">
              <button type="button" onClick={() => setAttachOpen(false)} className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
              <button data-testid="attach-confirm-btn" disabled={tsBusy || attachSel.length === 0} onClick={attachTs}
                className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md disabled:opacity-60">
                {tsBusy ? "Attaching…" : `Attach ${attachSel.length || ""} timesheet${attachSel.length === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {voidOpen && (
        <VoidInvoiceModal invoice={inv} onClose={() => setVoidOpen(false)} onVoided={() => { setVoidOpen(false); load(); loadTs(); }} />
      )}
    </div>
  );
}
